import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  EventEmitter,
  PLATFORM_ID,
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { take } from 'rxjs/operators';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { LoginPopupComponent } from 'src/app/shared/components/login-popup/login-popup.component';
import { BetService } from 'src/app/shared/services/bet.service';

@Component({
  selector: 'app-race-footer',
  templateUrl: './race-footer.component.html',
  styleUrls: ['./race-footer.component.scss'],
  animations: [
    // The keypad slides out of the bar it belongs to rather than appearing
    // fully formed, so the eye can follow where it came from. Height is
    // animated alongside opacity so the content above is pushed rather than
    // being covered abruptly. Easing is asymmetric on purpose: a touch slower
    // opening than closing, which reads as responsive rather than sluggish.
    trigger('keypadSlide', [
      transition(':enter', [
        style({ height: 0, opacity: 0, transform: 'translateY(12px)' }),
        animate(
          '220ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({ height: '*', opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '160ms cubic-bezier(0.4, 0, 1, 1)',
          style({ height: 0, opacity: 0, transform: 'translateY(12px)' })
        ),
      ]),
    ]),
  ],
})
// @Sentry.TraceClassDecorator()
export class RaceFooterComponent implements OnInit, OnChanges {
  @Input() cuponInfo;
  @Input() raceDate;
  @Input() isFormValid: boolean;
  @Input() selectedBet: any;
  @Input() bettingSlipStatus: any;
  @Output() clearAll: EventEmitter<any>;

  // Collapsed on first appearance: the slip is revealed automatically the moment
  // a horse is ticked, and opening it expanded shoved the runner list the user
  // was reading off screen. The summary, total, stake and place-bet button all
  // stay visible in the sticky bar; the chevron expands the detail on request.
  bettingInfo = false;
  ref: DynamicDialogRef;
  allBetsOnCart$ = this.betService.allAddedBetsInCart$.asObservable();

  // --- in-page stake keypad -------------------------------------------------
  // The OS keyboard covers half a phone screen, hides the very slip being
  // edited, and used to zoom the page. Our own pad keeps the bet in view.
  keypadOpen = false;
  keypadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'];
  stakeDraft = '';
  isTouchLayout = false;
  private stakeBeforeEdit = '';
  // True from the moment the pad opens until the first key. It is what makes
  // the first backspace mean "start this amount again" rather than "delete one
  // character" — see backspace().
  private freshEdit = false;

  constructor(
    public betService: BetService,
    public authService: AuthService,
    public dialogService: DialogService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.clearAll = new EventEmitter<any>(null);
  }

  // The component is never destroyed between races, so an open pad and its
  // draft would otherwise survive into the next race and could write a stake
  // chosen for a different bet.
  ngOnChanges(): void {
    if (!this.bettingSlipStatus && this.keypadOpen) {
      this.keypadOpen = false;
      this.stakeDraft = '';
      this.freshEdit = false;
    }
  }

  // A phone that is rotated, or a tablet that gains a keyboard, must not be
  // left with a field it can no longer edit.
  @HostListener('window:resize')
  onResize(): void {
    this.isTouchLayout = this.detectTouchLayout();
  }

  ngOnInit(): void {
    // Touch layout is decided once, in the browser only: this component is
    // server-rendered too, where there is no window to measure.
    this.isTouchLayout = this.detectTouchLayout();
  }

  // Tapping the field toggles the pad, so the same control that opens it also
  // puts it away — the field is the thing the customer associates with the pad,
  // and hunting for "Terminé" to dismiss it is a second thing to learn.
  // Coarse pointer AND small viewport: a tablet driven by a mouse or keyboard
  // keeps ordinary typing rather than being forced through the pad.
  private detectTouchLayout(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    const coarse =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(pointer: coarse)').matches
        : true;
    return coarse && window.innerWidth <= 1024;
  }

  toggleKeypad(): void {
    if (!this.isTouchLayout) {
      return; // desktop types normally
    }
    if (this.keypadOpen) {
      this.closeKeypad();
      return;
    }
    this.stakeBeforeEdit = this.currentStakeAsString();
    this.stakeDraft = this.stakeBeforeEdit;
    this.freshEdit = true;
    this.keypadOpen = true;
  }

  pressKey(key: string): void {
    this.freshEdit = false;
    if (key === '.') {
      if (this.stakeDraft.indexOf('.') > -1) {
        return; // only one decimal separator
      }
      this.stakeDraft = (this.stakeDraft || '0') + '.';
      return; // a trailing '.' is not yet a number — do not publish it
    }
    const next = this.stakeDraft + key;
    // Money here is 2-decimal; refuse a third rather than silently rounding
    // away a digit the customer just typed.
    const decimals = next.split('.')[1];
    if (decimals && decimals.length > 2) {
      return;
    }
    // Drop a leading zero ("05" -> "5") but keep "0." intact.
    this.stakeDraft = /^0[0-9]/.test(next) ? next.slice(1) : next;
    this.publishDraft();
  }

  backspace(): void {
    // First press after opening clears the whole amount instead of nibbling one
    // digit off it. Someone who opens the pad on 200 and taps erase wants to
    // type a new stake, not to bet 20 — and 20 is a stake they never chose, one
    // tap away from being placed. Every later press deletes normally, so 200
    // still becomes 20 mid-edit.
    if (this.freshEdit) {
      this.freshEdit = false;
      this.stakeDraft = '';
      this.setStake('');
      return;
    }
    this.stakeDraft = this.stakeDraft.slice(0, -1);
    this.publishDraft();
  }

  closeKeypad(): void {
    // Never silently rewrite the amount to something the customer did not
    // choose. An incomplete edit ("", "0.") reverts to what it was before they
    // opened the pad; anything else is published as typed and the form's own
    // validator decides whether the bet may be placed.
    const parsed = Number(this.stakeDraft);
    if (this.stakeDraft === '' || !Number.isFinite(parsed) || parsed <= 0) {
      this.setStake(this.stakeBeforeEdit);
    }
    this.keypadOpen = false;
  }

  private publishDraft(): void {
    // An emptied field publishes as empty so the input, the pad's readout and
    // the total all agree that no stake is set yet; the form's own validator
    // then gates the bet. A half-typed number like "0." is held back instead,
    // since it is not yet a value anything downstream can use.
    if (this.stakeDraft === '') {
      this.setStake('');
      return;
    }
    // "5." parses as 5 but is a half-typed value, not a chosen amount.
    if (
      this.stakeDraft.endsWith('.') ||
      !Number.isFinite(Number(this.stakeDraft))
    ) {
      return;
    }
    this.setStake(this.stakeDraft);
  }

  // Always write through the FORM CONTROL. Its valueChanges is what syncs the
  // stake that is actually submitted (RaceDetailsComponent.selectedStake) and
  // recomputes the total; writing to betService.selectedStake alone would
  // change the display without changing the bet.
  private setStake(value: string): void {
    this.betService.raceDetailsForm.controls.selectedStake.patchValue(value);
  }

  private currentStakeAsString(): string {
    const v = this.betService.raceDetailsForm.controls.selectedStake.value;
    return v === null || v === undefined ? '' : String(v);
  }

  showConfirmBetPopup() {
    this.bettingInfo = false;
    this.authService
      .getCurrentUser()
      .pipe(take(1))
      .subscribe((currentUser) => {
        if (!currentUser) {
          this.ref = this.dialogService.open(LoginPopupComponent, {
            showHeader: true,
            dismissableMask: true,
            closable: true,
            styleClass: 'login-modal',
            data: {
              reload: false,
            },
            contentStyle: {
              'border-radius': '4px',
              padding: '0',
              'z-index': '999',
            },
          });
          this.ref.onClose.subscribe((loginSuccess?) => {
            if (loginSuccess) {
              this.betService.showConfirmBetPopupEvent$.next();
            }
          });
        } else {
          this.betService.showConfirmBetPopupEvent$.next();
        }
      });
  }

  // The stake travels as a form-control value, so it can arrive as a string.
  // DecimalPipe throws on a non-numeric string, and the bet slip must never be
  // the thing that breaks the page. Coerce here and show 0.00 for garbage.
  unitStake(value: any): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  // The stake for the bet being built. `cuponInfo` is only refreshed when a bet
  // is pushed, so after the manual show/hide-ticket toggle it can still be
  // empty while the Total below is already correct — which printed
  // "1x 0.00 TND" above a real total. Both fields track the same form control,
  // so falling back keeps the two lines telling the same story.
  currentUnitStake(): number {
    const fromCupon = Number(this.cuponInfo && this.cuponInfo.selectedStake);
    if (Number.isFinite(fromCupon) && fromCupon > 0) {
      return fromCupon;
    }
    return this.unitStake(this.betService.selectedStake);
  }

  increase(): void {
    this.betService.increase();
  }

  decrease(): void {
    this.betService.decrease();
  }

  clearBetSlip() {
    this.bettingInfo = false;
    this.clearAll.emit(true);
  }
}
